import React, { useState, useEffect } from "react";
import { X, MapPin, Landmark, Globe } from "lucide-react";
import VibeFilterBar from "@/components/VibeFilterBar";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";

/**
 * Phase-4 Explore filter sheet.
 *
 * 2026-07-15 rev 3 — user feedback restructure:
 *   • PROPERTY TYPE moves to the TOP with the FULL pre-rebuild list
 *     of 16 property types (was 5 in Pass B).
 *   • ADDITIONAL FILTERS (vibes) is back INSIDE the sheet — the Pass B
 *     top-of-page pill row was cluttering the primary column.
 *   • Order: Property type → Location → Budget → Additional filters.
 *
 * Everything is *staged* — mutations only affect local draft state
 * until the user hits Apply. Reset clears everything AND closes.
 *
 * Props:
 *   open          → controlled visibility
 *   onClose       → cancel / dismiss (drops draft)
 *   value         → { vibes, minBudget, maxBudget, priceUnit,
 *                     propertyTypes, city, state, country, radius }
 *   onApply(draft) → user hit Apply — commit draft to parent state
 *   onReset()     → user hit Reset — clear all filters, close sheet
 */

const BUDGET_PERIODS = [
  { id: "per_night",  label: "Per night",  hint: "hotels · resorts" },
  { id: "per_person", label: "Per person", hint: "cruises · tours" },
  { id: "per_week",   label: "Per week",   hint: "rentals · cabins" },
  { id: "per_stay",   label: "Per stay",   hint: "short trips" },
];

// Full 16-property-type set (2026-07-15 rev 3 — restored per user).
// Grouped mentally: core stay-types first, then lifestyle qualifiers,
// then extended categories. All chips are toggle-selectable.
// 2026-08-14 — vocabulary moved to /constants/propertyTypes.js so the
// admin booking-override editor imports the same list.  Add new
// pills there; both surfaces pick them up automatically.
import { PROPERTY_TYPES } from "../constants/propertyTypes";

const DEFAULT_RADIUS = 25;

export default function PhaseFourFiltersSheet({
  open,
  onClose,
  value = {},
  onApply,
  onReset,
}) {
  const [vibes, setVibes] = useState(value.vibes || []);
  const [minBudget, setMinBudget] = useState(value.minBudget || "");
  const [maxBudget, setMaxBudget] = useState(value.maxBudget || "");
  const [priceUnit, setPriceUnit] = useState(value.priceUnit || "per_night");
  const [propertyTypes, setPropertyTypes] = useState(value.propertyTypes || []);
  const [city, setCity] = useState(value.city || "");
  const [stateVal, setStateVal] = useState(value.state || "");
  const [country, setCountry] = useState(value.country || "");
  const [radius, setRadius] = useState(
    typeof value.radius === "number" ? value.radius : DEFAULT_RADIUS
  );

  // Sync from parent when the sheet opens so it always seeds from the
  // latest committed state — never a stale draft from a previous
  // open+cancel cycle.
  useEffect(() => {
    if (open) {
      setVibes(value.vibes || []);
      setMinBudget(value.minBudget || "");
      setMaxBudget(value.maxBudget || "");
      setPriceUnit(value.priceUnit || "per_night");
      setPropertyTypes(value.propertyTypes || []);
      setCity(value.city || "");
      setStateVal(value.state || "");
      setCountry(value.country || "");
      setRadius(typeof value.radius === "number" ? value.radius : DEFAULT_RADIUS);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const toggleProp = (id) => {
    setPropertyTypes((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  if (!open) return null;

  const hasLocation = Boolean(city.trim() || stateVal.trim() || country.trim());

  const doApply = () => {
    onApply && onApply({
      vibes,
      minBudget: minBudget || "",
      maxBudget: maxBudget || "",
      priceUnit,
      propertyTypes,
      city: city.trim(),
      state: stateVal.trim(),
      country: country.trim(),
      radius: hasLocation ? radius : null,
    });
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-tb-navy/60"
      onClick={onClose}
      data-testid="explore-filters-sheet"
    >
      <div
        className="w-full md:max-w-lg bg-white rounded-t-3xl md:rounded-2xl border-t-2 md:border-2 border-tb-gold shadow-2xl max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 bg-tb-navy text-tb-gold-bright rounded-t-3xl md:rounded-t-xl">
          <h2 className="font-serif-it italic text-xl">Filters</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close filters"
            className="p-1 rounded-full hover:bg-white/10 tb-tap"
            data-testid="explore-filters-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 bg-white">
          <Accordion type="multiple" defaultValue={["property", "location", "budget"]}>

            {/* ── PROPERTY TYPE — top of the sheet ───────────────── */}
            <AccordionItem value="property" className="border-tb-cream-3">
              <AccordionTrigger className="font-serif-it text-lg text-tb-navy hover:no-underline">
                Property type
              </AccordionTrigger>
              <AccordionContent>
                <div className="flex flex-wrap gap-2 pt-1">
                  {PROPERTY_TYPES.map((p) => {
                    const active = propertyTypes.includes(p.id);
                    return (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => toggleProp(p.id)}
                        className={
                          "px-3 h-9 rounded-full border-2 font-hand text-sm tb-tap " +
                          (active
                            ? "bg-tb-navy text-tb-gold-bright border-tb-gold"
                            : "bg-white text-tb-navy border-tb-navy/30 hover:border-tb-navy/50")
                        }
                        data-testid={`explore-property-${p.id}`}
                      >
                        {p.label}
                      </button>
                    );
                  })}
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* ── LOCATION ────────────────────────────────────────── */}
            <AccordionItem value="location" className="border-tb-cream-3">
              <AccordionTrigger className="font-serif-it text-lg text-tb-navy hover:no-underline">
                Location
              </AccordionTrigger>
              <AccordionContent>
                <div className="pt-1 space-y-3">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                    {[
                      { key: "city",    val: city,      setter: setCity,     label: "City",    Icon: MapPin,   tid: "filter-sheet-city" },
                      { key: "state",   val: stateVal,  setter: setStateVal, label: "State",   Icon: Landmark, tid: "filter-sheet-state" },
                      { key: "country", val: country,   setter: setCountry,  label: "Country", Icon: Globe,    tid: "filter-sheet-country" },
                    ].map(({ key, val, setter, label, Icon, tid }) => (
                      <div key={key} className="relative">
                        <Icon className="w-4 h-4 text-tb-navy/50 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                        <input
                          data-testid={tid}
                          value={val}
                          onChange={(e) => setter(e.target.value)}
                          placeholder={label}
                          aria-label={label}
                          className="w-full pl-9 pr-3 h-11 rounded-xl bg-white border-2 border-tb-cream-3 focus:border-tb-gold outline-none font-ui text-sm text-tb-navy"
                        />
                      </div>
                    ))}
                  </div>
                  {hasLocation && (
                    <label className="block pt-1">
                      <div className="flex items-baseline justify-between mb-1">
                        <span className="font-hand text-tb-navy/65 text-xs">within</span>
                        <span data-testid="filter-sheet-radius-label" className="font-serif-it italic text-tb-navy text-sm">
                          {radius} mi
                        </span>
                      </div>
                      <input
                        data-testid="filter-sheet-radius"
                        type="range" min="5" max="100" step="5"
                        value={radius}
                        onChange={(e) => setRadius(parseInt(e.target.value, 10))}
                        className="w-full accent-tb-coral"
                        aria-label="Search radius in miles"
                      />
                    </label>
                  )}
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* ── BUDGET ────────────────────────────────────────── */}
            <AccordionItem value="budget" className="border-tb-cream-3">
              <AccordionTrigger className="font-serif-it text-lg text-tb-navy hover:no-underline">
                Budget
              </AccordionTrigger>
              <AccordionContent>
                <div className="pt-1 space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <label className="flex flex-col gap-1">
                      <span className="font-hand text-xs text-tb-navy/70">Min ($)</span>
                      <input
                        type="number"
                        inputMode="numeric"
                        value={minBudget}
                        onChange={(e) => setMinBudget(e.target.value.replace(/\D/g, ""))}
                        placeholder="Any"
                        data-testid="filter-sheet-budget-min"
                        className="h-10 px-3 rounded-lg bg-white border-2 border-tb-navy/20 text-tb-navy font-hand text-sm focus:border-tb-gold focus:outline-none focus:ring-2 focus:ring-tb-gold/25"
                      />
                    </label>
                    <label className="flex flex-col gap-1">
                      <span className="font-hand text-xs text-tb-navy/70">Max ($)</span>
                      <input
                        type="number"
                        inputMode="numeric"
                        value={maxBudget}
                        onChange={(e) => setMaxBudget(e.target.value.replace(/\D/g, ""))}
                        placeholder="Any"
                        data-testid="filter-sheet-budget-max"
                        className="h-10 px-3 rounded-lg bg-white border-2 border-tb-navy/20 text-tb-navy font-hand text-sm focus:border-tb-gold focus:outline-none focus:ring-2 focus:ring-tb-gold/25"
                      />
                    </label>
                  </div>
                  <div>
                    <div className="font-hand text-xs text-tb-navy/70 mb-1.5">
                      Period
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      {BUDGET_PERIODS.map((u) => (
                        <button
                          key={u.id}
                          type="button"
                          onClick={() => setPriceUnit(u.id)}
                          aria-selected={priceUnit === u.id}
                          data-testid={`filter-sheet-price-unit-${u.id}`}
                          className={
                            "px-3 h-11 rounded-lg border-2 text-left font-hand text-xs tb-tap " +
                            (priceUnit === u.id
                              ? "bg-tb-navy text-tb-gold-bright border-tb-gold"
                              : "bg-white text-tb-navy border-tb-navy/20 hover:border-tb-navy/50")
                          }
                        >
                          <div className="font-semibold">{u.label}</div>
                          <div className={priceUnit === u.id ? "text-tb-gold-bright/70" : "text-tb-navy/50"}>
                            {u.hint}
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* ── ADDITIONAL FILTERS section removed (2026-07-15) —
                user reported it as a duplicate of the Property Type
                section. Vibes selection continues to persist via the
                parent's `vibes` state, which is empty by default and
                populated only when Property Type chips carry vibe tags. */}

          </Accordion>
        </div>

        <div className="flex items-center gap-3 px-5 py-4 border-t-2 border-tb-cream-3 bg-white">
          <button
            type="button"
            onClick={onReset}
            data-testid="explore-filters-reset"
            className="flex-1 h-11 rounded-full border-2 border-tb-navy/40 bg-white text-tb-navy font-hand hover:border-tb-navy tb-tap"
          >
            Reset
          </button>
          <button
            type="button"
            onClick={doApply}
            data-testid="explore-filters-apply"
            className="flex-1 h-11 rounded-full bg-tb-navy text-tb-gold-bright font-hand hover:brightness-110 tb-tap"
          >
            Apply
          </button>
        </div>
      </div>
    </div>
  );
}
